// packages/web/src/lib/audioSupport.ts — CV5 §5: CAN THIS DEVICE PLAY THIS
// RECORDING, AND IF NOT, SAY SO.
//
// THE PLATFORM FACT (verified 2026-08-16, and the reason this module exists):
// WKWebView — which is EVERY browser and every installed PWA on iOS, Safari
// and Chrome alike — ships no WebM/Opus decoder. `MediaRecorder` on iOS Safari
// therefore does not produce WebM either: it produces `audio/mp4` (AAC), which
// iOS plays. The practical matrix for a recording made by our own hold:
//
//   recorded on            container/codec        plays on iOS   plays on Chromium
//   iOS Safari / iOS PWA   audio/mp4 (AAC)        yes            yes
//   desktop Chromium       audio/webm;codecs=opus NO             yes
//   desktop Firefox        audio/ogg or webm/opus NO             yes (webm)
//
// So a tech's OWN recordings play on the tech's OWN phone — the ordinary case
// is fine. What is NOT fine is the cross-device case (a note recorded at a
// desk, opened in the field) rendering a silent black rectangle, which is what
// `<iframe src=blob:audio/webm>` did before this chunk. `canPlayType` is the
// browser's own answer to exactly this question, so we ask it and render the
// answer instead of a broken control.
//
// `HTMLMediaElement.canPlayType` returns '' (no), 'maybe', or 'probably'. Only
// '' is a refusal; 'maybe' is the normal answer for a container whose codecs
// were not spelled out, and must be treated as playable — treating 'maybe' as
// "no" would hide a perfectly good player on most browsers.

export type AudioPlayability = 'playable' | 'unsupported' | 'unknown';

/** Whether this engine claims it can decode `mimeType`. 'unknown' when there
 * is no audio element to ask (jsdom, SSR) — the caller renders the player,
 * because refusing to draw a control on the strength of a missing API would
 * be a worse lie than drawing one that turns out silent. */
export function audioPlayability(mimeType: string): AudioPlayability {
  if (typeof document === 'undefined' || typeof document.createElement !== 'function') return 'unknown';
  let el: HTMLAudioElement;
  try {
    el = document.createElement('audio');
  } catch {
    return 'unknown';
  }
  if (typeof el.canPlayType !== 'function') return 'unknown';
  const base = mimeType.split(';')[0]!.trim().toLowerCase();
  // Ask about the full type first (it may carry `codecs=opus`, which is the
  // precise question), then the bare container.
  const answers = [mimeType, base].map((t) => {
    try {
      return el.canPlayType(t);
    } catch {
      return '';
    }
  });
  if (answers.some((a) => a === 'probably' || a === 'maybe')) return 'playable';
  return 'unsupported';
}

const AUDIO_EXT = /\.(webm|m4a|mp4|mp3|wav|ogg|oga|aac|flac|opus)$/i;

/** Whether a stored filename reads as audio — the pre-fetch gate for surfaces
 * that only know a filename (the job row's thumb), never a substitute for the
 * served mime type where one is in hand. */
export function looksLikeAudio(filename: string | null | undefined): boolean {
  return typeof filename === 'string' && AUDIO_EXT.test(filename);
}
